/**
 * Live calls — a real-time monitoring board. Active calls (LIVE rows + live
 * timer) are pinned above recent ended-call records; selecting a call shows
 * either the live transcript/handoff panel (active) or a read-only summary
 * (recent). Provider-independent: only canonical Hostesa call data + the
 * existing realtime subscriptions. Caller identity is honest — the backend has
 * no name/phone yet, so rows lead with direction · language + a shortId monogram.
 */

import { type Call, useCalls, useCallTranscript, useRequestHandoff } from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  EmptyState,
  ErrorState,
  LanguagePill,
  LoadingState,
  PageHeader,
} from '@hostesa/ui';
import { ArrowRight } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import {
  canRequestHandoff,
  durationSeconds,
  isCallActive,
  languageLabel,
  languagePillCode,
  optionalText,
  shortId,
} from './components/call-presentation';
import { CallStatusBadge } from './components/call-status-badge';
import { HandoffAudioPanel } from './components/handoff-audio-panel';
import { CallRoomSubscription, HotelRoomSubscription } from './components/realtime-subscriptions';
import { TranscriptPanel } from './components/transcript-panel';
import { useElapsed } from './use-elapsed';

// The shared app-shell container owns width + padding (spec 45).
const PAGE_WRAP = '';

export interface LiveCallsPageProps {
  /** Navigate to a call's full detail page. Wired by the router-level route. */
  onViewCall?: (callId: string) => void;
}

/** Two-char monogram from a call id (no caller name exists in the backend yet). */
function monogram(id: string): string {
  return (
    id
      .replace(/[^a-z0-9]/gi, '')
      .slice(0, 2)
      .toUpperCase() || '··'
  );
}

/** Best available "call began" timestamp for the live timer. */
function startedAtOf(call: Call): string | null {
  return (
    optionalText(call.answeredAt) ?? optionalText(call.startedAt) ?? optionalText(call.createdAt)
  );
}

export function LiveCallsPage({ onViewCall }: LiveCallsPageProps = {}) {
  const { t } = useT('calls');
  const { tenantId, hotelId } = useHotelContext();
  const callsQuery = useCalls(tenantId, hotelId, { limit: 50 });

  const active = useMemo<Call[]>(() => {
    const at = (c: Call) => new Date(c.answeredAt ?? c.startedAt ?? c.createdAt).getTime();
    return (callsQuery.data?.items ?? [])
      .filter((c) => isCallActive(c.status))
      .sort((a, b) => at(a) - at(b));
  }, [callsQuery.data]);
  const recent = useMemo<Call[]>(() => {
    const at = (c: Call) => new Date(c.endedAt ?? c.startedAt ?? c.createdAt).getTime();
    return (callsQuery.data?.items ?? [])
      .filter((c) => !isCallActive(c.status))
      .sort((a, b) => at(b) - at(a));
  }, [callsQuery.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo<Call | null>(
    () => [...active, ...recent].find((c) => c.id === selectedId) ?? active[0] ?? recent[0] ?? null,
    [active, recent, selectedId],
  );

  return (
    <section data-testid="live-calls-page" className={PAGE_WRAP}>
      <HotelRoomSubscription tenantId={tenantId} hotelId={hotelId} />
      <PageHeader
        eyebrow={t('live.eyebrow')}
        title={t('live.title')}
        description={t('live.description')}
        actions={<LiveStatusLine count={active.length} />}
      />

      {callsQuery.isLoading ? (
        <LoadingState data-testid="live-calls-loading" title={t('history.loading')} />
      ) : callsQuery.isError ? (
        <ErrorState
          data-testid="live-calls-error"
          title={t('history.error.title')}
          description={t('history.error.description')}
        />
      ) : active.length === 0 && recent.length === 0 ? (
        <EmptyState
          data-testid="live-calls-empty"
          title={t('live.activeList.empty.title')}
          description={t('live.activeList.empty.description')}
        />
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[20rem_1fr]">
          <aside data-testid="live-calls-list" className="flex flex-col gap-5">
            {active.length > 0 && (
              <CallGroup title={t('live.activeList.title')}>
                {active.map((c) => (
                  <LiveCallRow
                    key={c.id}
                    call={c}
                    selected={selected?.id === c.id}
                    onSelect={() => setSelectedId(c.id)}
                  />
                ))}
              </CallGroup>
            )}
            {recent.length > 0 && (
              <CallGroup title={t('live.recentList.title')}>
                {recent.map((c) => (
                  <RecentCallRow
                    key={c.id}
                    call={c}
                    selected={selected?.id === c.id}
                    onSelect={() => setSelectedId(c.id)}
                  />
                ))}
              </CallGroup>
            )}
          </aside>

          {selected ? (
            isCallActive(selected.status) ? (
              <LiveCallDetail key={selected.id} call={selected} />
            ) : (
              <RecentCallSummary key={selected.id} call={selected} onViewCall={onViewCall} />
            )
          ) : (
            <EmptyState
              data-testid="live-call-panel-empty"
              title={t('live.panel.selectPrompt.title')}
              description={t('live.panel.selectPrompt.description')}
            />
          )}
        </div>
      )}
    </section>
  );
}

function LiveStatusLine({ count }: { count: number }) {
  const { t } = useT('calls');
  const live = count > 0;
  return (
    <span
      data-testid="live-status"
      className={cn(
        'flex items-center gap-2 text-sm',
        live ? 'font-medium text-[var(--color-charcoal)]' : 'text-[var(--color-stone-mid)]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'size-2 rounded-full',
          live ? 'animate-pulse bg-[var(--color-alert)]' : 'bg-[var(--color-stone-400)]',
        )}
      />
      {live ? t('live.liveNow', { count }) : t('live.idle')}
    </span>
  );
}

function CallGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)]">
        {title}
      </h2>
      <ul className="flex flex-col gap-1.5">{children}</ul>
    </div>
  );
}

function rowClass(selected: boolean): string {
  return cn(
    'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
    selected
      ? 'border-[var(--color-warm-stone)] bg-[var(--color-canvas)]'
      : 'border-[var(--color-stone-200)] bg-[var(--color-alabaster)] hover:bg-[var(--color-stone-100)]',
  );
}

function LiveCallRow({
  call,
  selected,
  onSelect,
}: {
  call: Call;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useT('calls');
  const { timezone } = useHotelContext();
  const { formatDuration } = useFormatters(timezone);
  const elapsed = useElapsed(startedAtOf(call));
  return (
    <li>
      <button
        type="button"
        data-testid={`live-call-item-${call.id}`}
        aria-pressed={selected}
        onClick={onSelect}
        className={rowClass(selected)}
      >
        <span
          aria-hidden="true"
          className="size-2 shrink-0 animate-pulse rounded-full bg-[var(--color-alert)]"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <span className="font-[var(--font-mono)] text-[10px] font-semibold uppercase tracking-wider text-[var(--color-alert)]">
              {t('live.liveBadge')}
            </span>
            <LanguagePill code={languagePillCode(call.language)} />
          </span>
          <span className="truncate text-sm text-[var(--color-charcoal)]">
            {t(`live.status.${call.status}`, { defaultValue: t('live.status.created') })}
          </span>
        </span>
        <span
          data-testid={`live-call-timer-${call.id}`}
          role="timer"
          aria-label={t('live.elapsedLabel', { time: formatDuration(elapsed) })}
          className="shrink-0 font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]"
        >
          {formatDuration(elapsed)}
        </span>
      </button>
    </li>
  );
}

function RecentCallRow({
  call,
  selected,
  onSelect,
}: {
  call: Call;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useT('calls');
  const { timezone } = useHotelContext();
  const { formatDuration, formatRelativeTime } = useFormatters(timezone);
  const dur = durationSeconds(call.durationSeconds);
  const ok = call.status === 'completed';
  const ended = optionalText(call.endedAt) ?? optionalText(call.startedAt);
  return (
    <li>
      <button
        type="button"
        data-testid={`recent-call-item-${call.id}`}
        aria-pressed={selected}
        onClick={onSelect}
        className={rowClass(selected)}
      >
        <span
          aria-hidden="true"
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-stone-100)] text-[11px]',
            ok ? 'text-[var(--color-stone-700)]' : 'text-[var(--color-alert)]',
          )}
        >
          {ok ? '✓' : '✗'}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <LanguagePill code={languagePillCode(call.language)} />
            <span className="text-sm text-[var(--color-charcoal)]">
              {t(`live.outcome.${ok ? 'completed' : 'failed'}`)}
            </span>
          </span>
          {ended && (
            <span className="text-xs text-[var(--color-stone-mid)]">
              {formatRelativeTime(ended)}
            </span>
          )}
        </span>
        <span className="shrink-0 font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]">
          {dur !== null ? formatDuration(dur) : t('common.notAvailable')}
        </span>
      </button>
    </li>
  );
}

function CallIdentityHeader({ call, trailing }: { call: Call; trailing?: ReactNode }) {
  const { t } = useT('calls');
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-stone-100)] font-[var(--font-mono)] text-xs font-semibold text-[var(--color-stone-700)]"
      >
        {monogram(call.id)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-[var(--font-display)] text-base font-medium text-[var(--color-charcoal)]">
          {t(`direction.${call.direction}`, { defaultValue: call.direction })} ·{' '}
          {languageLabel(call.language, t('common.notAvailable'))}
        </span>
        <span className="flex items-center gap-2">
          <CallStatusBadge status={call.status} data-testid="live-call-ai-status" />
          <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
            {shortId(call.id)}
          </span>
        </span>
      </div>
      {trailing}
    </div>
  );
}

function LiveCallDetail({ call }: { call: Call }) {
  const { t } = useT('calls');
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { formatDuration } = useFormatters(timezone);
  const transcriptQuery = useCallTranscript(tenantId, hotelId, call.id);
  const handoff = useRequestHandoff(tenantId, hotelId);
  const handoffAllowed = canRequestHandoff(call.status);
  const elapsed = useElapsed(startedAtOf(call));

  return (
    <div data-testid="live-call-panel" className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_18rem]">
      <CallRoomSubscription tenantId={tenantId} hotelId={hotelId} callId={call.id} />

      <Card className="flex flex-col">
        <CardHeader>
          <CallIdentityHeader
            call={call}
            trailing={
              <span className="shrink-0 font-[var(--font-mono)] text-sm text-[var(--color-stone-mid)]">
                {formatDuration(elapsed)}
              </span>
            }
          />
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <h3 className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]">
            {t('live.transcript.title')}
          </h3>
          {transcriptQuery.isLoading ? (
            <LoadingState data-testid="live-transcript-loading" />
          ) : transcriptQuery.isError ? (
            <ErrorState
              data-testid="live-transcript-error"
              title={t('detail.error.title')}
              description={t('detail.error.description')}
            />
          ) : (
            <div className="max-h-[28rem] overflow-y-auto pr-1">
              <TranscriptPanel
                data-testid="live-transcript"
                segments={transcriptQuery.data ?? []}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <aside className="flex flex-col gap-4">
        <Card data-testid="draft-reservation-card">
          <CardHeader>
            <CardTitle>{t('live.draft.title')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-[var(--color-stone-mid)]">
            {t('live.draft.placeholder')}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            data-testid="request-handoff-btn"
            variant="secondary"
            disabled={!handoffAllowed || handoff.isPending}
            onClick={() => handoff.mutate({ callId: call.id })}
          >
            {handoff.isPending ? t('live.handoff.requesting') : t('live.handoff.button')}
          </Button>
          {!handoffAllowed && (
            <p data-testid="handoff-unavailable" className="text-xs text-[var(--color-stone-mid)]">
              {call.status === 'handoff_requested'
                ? t('live.handoff.requested')
                : t('live.handoff.unavailable')}
            </p>
          )}
          {handoff.isError && (
            <p data-testid="handoff-error" className="text-xs text-[var(--color-alert)]">
              {t('live.handoff.error')}
            </p>
          )}
          {handoff.isSuccess && (
            <p data-testid="handoff-requested" className="text-xs text-[var(--color-success)]">
              {t('live.handoff.requested')}
            </p>
          )}
        </div>

        <HandoffAudioPanel
          callId={call.id}
          handoffActive={call.status === 'handoff_requested' || call.status === 'human_active'}
        />
      </aside>
    </div>
  );
}

function RecentCallSummary({
  call,
  onViewCall,
}: {
  call: Call;
  onViewCall?: (callId: string) => void;
}) {
  const { t } = useT('calls');
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { formatDuration, formatDateTime } = useFormatters(timezone);
  const transcriptQuery = useCallTranscript(tenantId, hotelId, call.id);
  const dur = durationSeconds(call.durationSeconds);
  const started = optionalText(call.startedAt);
  const ended = optionalText(call.endedAt);
  const endReason = optionalText(call.endReason);

  return (
    <Card data-testid="recent-call-summary" className="flex flex-col">
      <CardHeader>
        <CallIdentityHeader
          call={call}
          trailing={
            <span className="shrink-0 font-[var(--font-mono)] text-sm text-[var(--color-stone-mid)]">
              {dur !== null ? formatDuration(dur) : t('common.notAvailable')}
            </span>
          }
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-[var(--color-stone-mid)]">{t('live.recentDetail.language')}</dt>
          <dd>{languageLabel(call.language, t('common.notAvailable'))}</dd>
          <dt className="text-[var(--color-stone-mid)]">{t('live.recentDetail.direction')}</dt>
          <dd>{t(`direction.${call.direction}`, { defaultValue: call.direction })}</dd>
          <dt className="text-[var(--color-stone-mid)]">{t('live.recentDetail.started')}</dt>
          <dd>{started ? formatDateTime(started) : t('common.notAvailable')}</dd>
          <dt className="text-[var(--color-stone-mid)]">{t('live.recentDetail.ended')}</dt>
          <dd>{ended ? formatDateTime(ended) : t('common.notAvailable')}</dd>
          <dt className="text-[var(--color-stone-mid)]">{t('live.recentDetail.endReason')}</dt>
          <dd>{endReason ?? t('common.notAvailable')}</dd>
        </dl>

        <div>
          <h3 className="mb-2 font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]">
            {t('live.transcript.title')}
          </h3>
          {transcriptQuery.isLoading ? (
            <LoadingState data-testid="recent-transcript-loading" />
          ) : transcriptQuery.isError ? (
            <ErrorState
              data-testid="recent-transcript-error"
              title={t('detail.error.title')}
              description={t('detail.error.description')}
            />
          ) : (
            <div className="max-h-[24rem] overflow-y-auto pr-1">
              <TranscriptPanel
                data-testid="recent-transcript"
                segments={transcriptQuery.data ?? []}
              />
            </div>
          )}
        </div>

        {onViewCall && (
          <div>
            <Button
              type="button"
              variant="ghost"
              data-testid="recent-call-view-details"
              onClick={() => onViewCall(call.id)}
            >
              {t('live.recentDetail.viewFull')}
              <ArrowRight aria-hidden="true" className="ml-1 size-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
